import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, Radar, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import EmptyState from '../EmptyState';

/**
 * BD-16 — the Relationship Radar tab: at most five people worth a note this week, each with why now (evidence,
 * never a bare score), the last real interaction, something useful to offer and who knows them best. Draft a
 * note goes to Approvals (never sent). Useful / Not now / Not for this person tune next week's list.
 *
 * The page only ever asks for the signed-in person's own radar. The firm view (admins) lists top accounts that
 * nobody has touched lately and carries no person detail.
 */

interface Evidence { text: string; quote?: string; url?: string; date?: string; source: string }
interface Trigger { type: string; label: string; evidence: Evidence[]; commitment_id?: number }
interface Item {
  id: number;
  rank: number;
  status: string;
  draft_action_id: number | null;
  person: { id: number; name: string; title: string | null; org_name: string | null };
  triggers: Trigger[];
  last_interaction: { kind: string; title: string | null; at: string } | null;
  value_add: { kind: string; text: string; source?: { label: string; url: string | null } } | null;
  warm_path: {
    you: { interactions: number; last_at: string | null };
    owner: { name: string; is_you: boolean } | null;
    colleagues: { name: string; interactions: number; last_at: string | null }[];
  } | null;
}
interface Radar { opted_in: boolean; week_start: string; run: { generated_at: string } | null; items: Item[] }
interface FirmAccount { org_id: number; name: string; open_deals: number; open_value: number; last_touch_at: string | null; days_since: number | null }

const day = (iso: string | null | undefined) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

const ItemCard: React.FC<{ item: Item; busy: boolean; onDraft: (i: Item) => void; onFeedback: (i: Item, kind: string) => void; onDone: (id: number) => void }> = ({ item, busy, onDraft, onFeedback, onDone }) => (
  <li className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800" data-testid={`radar-item-${item.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">{item.rank}. {item.person.name}</h3>
        <p className="text-sm text-gray-500">{[item.person.title, item.person.org_name].filter(Boolean).join(' · ')}</p>
      </div>
      {item.draft_action_id && (
        <Link className="text-sm text-blue-600 hover:underline" to="/app/business-development?tab=approvals">Note waiting in Approvals</Link>
      )}
    </div>

    <div className="mt-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Why now</p>
      <ul className="mt-1 space-y-2">
        {item.triggers.map((t) => (
          <li key={t.type} className="text-sm text-gray-800 dark:text-gray-200">
            <span className="inline-block text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5 mr-2">{t.label}</span>
            {t.evidence.map((e, i) => (
              <span key={i} className="block ml-0 mt-1">
                {e.text}
                {e.quote && <q className="block text-gray-500 italic mt-0.5">{e.quote}</q>}
                {e.url && (
                  <a href={e.url} target="_blank" rel="noopener noreferrer" className="ml-1 inline-flex items-center text-blue-600 hover:underline">
                    source <ExternalLink className="w-3 h-3 ml-0.5" />
                  </a>
                )}
              </span>
            ))}
            {t.commitment_id && (
              <button type="button" className="ml-2 text-xs text-gray-500 underline" disabled={busy} onClick={async () => {
                try { await api.post(`/api/bd/radar/commitments/${t.commitment_id}/status`, { status: 'done' }); toast.success('Marked done'); onDone(item.id); } catch { toast.error('Could not update that'); }
              }}>Mark done</button>
            )}
          </li>
        ))}
      </ul>
    </div>

    <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
      <div>
        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Last interaction</dt>
        <dd>{item.last_interaction ? `${item.last_interaction.kind}${item.last_interaction.title ? ` "${item.last_interaction.title}"` : ''}, ${day(item.last_interaction.at)}` : 'None on record'}</dd>
      </div>
      <div>
        <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">Who knows them</dt>
        <dd>
          {item.warm_path?.owner ? `${item.warm_path.owner.is_you ? 'You own' : `${item.warm_path.owner.name} owns`} the relationship` : 'No owner set'}
          {item.warm_path?.colleagues?.length ? `; also ${item.warm_path.colleagues.map((c) => c.name).join(', ')}` : ''}
        </dd>
      </div>
    </dl>

    {item.value_add && (
      <p className="mt-3 text-sm"><span className="font-semibold">Offer: </span>{item.value_add.text}
        {item.value_add.source?.url && <a href={item.value_add.source.url} target="_blank" rel="noopener noreferrer" className="ml-1 text-blue-600 hover:underline">link</a>}
      </p>
    )}

    <div className="mt-4 flex flex-wrap gap-2">
      <button type="button" disabled={busy || !!item.draft_action_id} onClick={() => onDraft(item)} className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white disabled:opacity-50">
        {item.draft_action_id ? 'Note drafted' : 'Draft a note'}
      </button>
      <button type="button" disabled={busy || item.status === 'useful'} onClick={() => onFeedback(item, 'useful')} className="px-3 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-50">{item.status === 'useful' ? 'Marked useful' : 'Useful'}</button>
      <button type="button" disabled={busy} onClick={() => onFeedback(item, 'not_now')} className="px-3 py-1.5 text-sm rounded border border-gray-300">Not now</button>
      <button type="button" disabled={busy} onClick={() => onFeedback(item, 'not_for_person')} className="px-3 py-1.5 text-sm rounded border border-gray-300">Not for this person</button>
    </div>
  </li>
);

const RadarTab: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = ['ADMIN', 'MASTER', 'SUPER_ADMIN'].includes(user?.role || '');
  const [data, setData] = useState<Radar | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [building, setBuilding] = useState(false);
  const [firm, setFirm] = useState<{ no_touch_days: number; accounts: FirmAccount[] } | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/api/bd/radar');
      setData(res.data);
    } catch {
      toast.error('Could not load the radar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/api/bd/radar/firm').then((r) => setFirm({ no_touch_days: r.data.no_touch_days, accounts: r.data.accounts || [] })).catch(() => setFirm(null));
  }, [isAdmin]);

  const build = async (refresh: boolean) => {
    setBuilding(true);
    try {
      const res = await api.post('/api/bd/radar/build', { refresh });
      setData(res.data);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not build the radar');
    } finally {
      setBuilding(false);
    }
  };

  const draft = async (item: Item) => {
    setBusyId(item.id);
    try {
      await api.post(`/api/bd/radar/items/${item.id}/draft`);
      toast.success('Note is waiting in Approvals');
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not draft the note');
    } finally {
      setBusyId(null);
    }
  };

  const feedback = async (item: Item, kind: string) => {
    setBusyId(item.id);
    try {
      await api.post(`/api/bd/radar/items/${item.id}/feedback`, { kind });
      toast.success(kind === 'useful' ? 'Noted, thank you' : 'Okay, left out for a while');
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not save that');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

  const items = data?.items || [];
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Relationship Radar</h2>
          <p className="text-sm text-gray-500">Week of {data?.week_start}. Up to five people worth a note, and why.</p>
        </div>
        {data?.opted_in && (
          <button type="button" disabled={building} onClick={() => build(!!data?.run)} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-gray-300">
            {building ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} {data?.run ? 'Refresh' : 'Build this week'}
          </button>
        )}
      </div>

      {!data?.opted_in ? (
        <EmptyState icon={Radar} title="The Relationship Radar is off" description="Turn on the Relationship Radar step in BD Assistant settings to get a short weekly list of who needs attention." />
      ) : !data.run ? (
        <EmptyState icon={Radar} title="No radar yet this week" description="Your radar is made on Monday morning. You can also build it now." />
      ) : items.length === 0 ? (
        <EmptyState icon={Radar} title="Nobody needs attention right now" description="Nothing needs a nudge this week: no overdue promises, quiet deals or news worth a note." />
      ) : (
        <ul className="space-y-4">
          {items.map((it) => (
            <ItemCard key={it.id} item={it} busy={busyId === it.id} onDraft={draft} onFeedback={feedback} onDone={() => load()} />
          ))}
        </ul>
      )}

      {isAdmin && firm && (
        <section>
          <h3 className="font-semibold">Top accounts with no contact in {firm.no_touch_days}+ days</h3>
          {firm.accounts.length === 0 ? (
            <p className="text-sm text-gray-500 mt-1">Every top account has been touched recently.</p>
          ) : (
            <ul className="mt-2 divide-y divide-gray-100 dark:divide-gray-700 text-sm">
              {firm.accounts.map((a) => (
                <li key={a.org_id} className="py-2 flex justify-between">
                  <span>{a.name} · {a.open_deals} open deal{a.open_deals === 1 ? '' : 's'}</span>
                  <span className="text-gray-500">{a.last_touch_at ? `last contact ${a.last_touch_at} (${a.days_since} days)` : 'no contact on record'}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
};

export default RadarTab;
